import React, { useState, useEffect, useCallback } from 'react';
import { getFullApiUrl } from '../config';
import { addAuthHeader } from '../utils/auth';

const MODULE_LABELS = [
  { num: 1, name: 'Likuiditas & Arus Kas', icon: 'fa-water', color: 'blue' },
  { num: 2, name: 'Kemampuan Bayar Cicilan', icon: 'fa-money-bill-transfer', color: 'green' },
  { num: 3, name: 'Profitabilitas', icon: 'fa-chart-line', color: 'purple' },
  { num: 4, name: 'Struktur Modal', icon: 'fa-building-columns', color: 'orange' },
  { num: 5, name: 'Kualitas Piutang', icon: 'fa-file-invoice-dollar', color: 'teal' },
  { num: 6, name: 'Faktor Kualitatif', icon: 'fa-clipboard-check', color: 'pink' },
  { num: 7, name: 'Credit Score Final', icon: 'fa-star', color: 'yellow' },
];

const PROGRESS_MESSAGES = [
  '', 
  'Menganalisis Likuiditas...',
  'Menganalisis Kemampuan Bayar Cicilan...',
  'Menganalisis Profitabilitas...',
  'Menganalisis Struktur Modal...',
  'Menganalisis Kualitas Piutang...',
  'Membaca Laporan Auditor & Faktor Kualitatif...',
  'Menghitung Credit Score Final...',
];

const getRiskBadge = (klasifikasi) => {
  if (!klasifikasi) return { color: 'gray', label: 'N/A' };
  const k = klasifikasi.toUpperCase();
  if (k.includes('LOW')) return { color: 'green', label: 'LOW RISK', bg: 'bg-green-100 dark:bg-green-900/30', text: 'text-green-700 dark:text-green-300', border: 'border-green-300 dark:border-green-600' };
  if (k.includes('MEDIUM')) return { color: 'yellow', label: 'MEDIUM RISK', bg: 'bg-yellow-100 dark:bg-yellow-900/30', text: 'text-yellow-700 dark:text-yellow-300', border: 'border-yellow-300 dark:border-yellow-600' };
  if (k.includes('HIGH') && !k.includes('CRITICAL')) return { color: 'orange', label: 'HIGH RISK', bg: 'bg-orange-100 dark:bg-orange-900/30', text: 'text-orange-700 dark:text-orange-300', border: 'border-orange-300 dark:border-orange-600' };
  if (k.includes('CRITICAL')) return { color: 'red', label: 'CRITICAL RISK', bg: 'bg-red-100 dark:bg-red-900/30', text: 'text-red-700 dark:text-red-300', border: 'border-red-300 dark:border-red-600' };
  return { color: 'gray', label: klasifikasi, bg: 'bg-gray-100 dark:bg-gray-700', text: 'text-gray-700 dark:text-gray-300', border: 'border-gray-300 dark:border-gray-600' };
};

const getRecommendationStyle = (rec) => {
  if (!rec) return { bg: 'bg-gray-50', border: 'border-gray-200', icon: 'fa-question', iconColor: 'text-gray-400' };
  const r = rec.toUpperCase();
  if (r.includes('CONDITIONAL')) return { bg: 'bg-amber-50 dark:bg-amber-900/20', border: 'border-amber-300 dark:border-amber-600', icon: 'fa-triangle-exclamation', iconColor: 'text-amber-500' };
  if (r.includes('APPROVE')) return { bg: 'bg-green-50 dark:bg-green-900/20', border: 'border-green-300 dark:border-green-600', icon: 'fa-circle-check', iconColor: 'text-green-500' };
  return { bg: 'bg-red-50 dark:bg-red-900/20', border: 'border-red-300 dark:border-red-600', icon: 'fa-circle-xmark', iconColor: 'text-red-500' };
};

const getScoreColor = (score) => {
  if (score >= 75) return '#22c55e';
  if (score >= 50) return '#eab308';
  if (score >= 25) return '#f97316';
  return '#ef4444';
};

// ============ CREDIT SCORE GAUGE ============
const CreditScoreGauge = ({ score }) => {
  const numScore = typeof score === 'number' ? score : parseFloat(score) || 0;
  const clampedScore = Math.min(100, Math.max(0, numScore));
  const angle = (clampedScore / 100) * 180;
  const color = getScoreColor(clampedScore);
  
  return (
    <div className="flex flex-col items-center">
      <svg viewBox="0 0 200 120" className="w-56 h-32">
        {/* Background arc */}
        <path d="M 20 100 A 80 80 0 0 1 180 100" fill="none" stroke="#e5e7eb" strokeWidth="16" strokeLinecap="round" />
        {/* Score arc */}
        <path
          d="M 20 100 A 80 80 0 0 1 180 100"
          fill="none"
          stroke={color}
          strokeWidth="16"
          strokeLinecap="round"
          strokeDasharray={`${(angle / 180) * 251.2} 251.2`}
          style={{ transition: 'stroke-dasharray 1.5s ease-out' }}
        />
        {/* Score text */}
        <text x="100" y="90" textAnchor="middle" fontSize="32" fontWeight="bold" fill={color}>
          {Math.round(clampedScore)}
        </text>
        <text x="100" y="108" textAnchor="middle" fontSize="11" fill="#6b7280">/ 100</text>
      </svg>
    </div>
  );
};

// ============ MODULE DETAIL ACCORDION ============
const ModuleAccordion = ({ moduleNum, result, label }) => {
  const [isOpen, setIsOpen] = useState(false);
  if (!result || result.error) return null;
  
  const skor = result.skor || 0;
  const scoreColor = getScoreColor(skor);
  
  return (
    <div className="border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden transition-colors">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
      >
        <div className="flex items-center space-x-3">
          <i className={`fa-solid ${label.icon} text-${label.color}-500 w-5`}></i>
          <span className="font-medium text-gray-800 dark:text-white text-sm">{label.name}</span>
        </div>
        <div className="flex items-center space-x-3">
          <span className="text-sm font-bold" style={{ color: scoreColor }}>{skor}</span>
          <i className={`fa-solid fa-chevron-${isOpen ? 'up' : 'down'} text-gray-400 text-xs`}></i>
        </div>
      </button>
      {isOpen && (
        <div className="px-4 py-4 bg-white dark:bg-gray-800 space-y-3 transition-colors">
          {/* Temuan */}
          <div>
            <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Temuan</h5>
            <p className="text-sm text-gray-700 dark:text-gray-300">{result.temuan || '-'}</p>
          </div>
          {/* Rasio */}
          {result.rasio && (
            <div>
              <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Rasio</h5>
              <div className="bg-gray-50 dark:bg-gray-700/50 rounded p-3 space-y-1">
                {Object.entries(result.rasio).map(([key, val]) => (
                  <div key={key} className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400 capitalize">{key.replace(/_/g, ' ')}</span>
                    <span className="font-medium text-gray-800 dark:text-white">{typeof val === 'object' ? JSON.stringify(val) : String(val)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Opini Auditor (Module 6) */}
          {result.opini_auditor && (
            <div>
              <h5 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase mb-1">Opini Auditor</h5>
              <p className="text-sm text-gray-700 dark:text-gray-300">{result.opini_auditor}</p>
            </div>
          )}
          {/* Red Flags */}
          {result.red_flags && result.red_flags.length > 0 && (
            <div>
              <h5 className="text-xs font-semibold text-red-500 uppercase mb-1">Red Flags</h5>
              <ul className="space-y-1">
                {result.red_flags.map((flag, i) => (
                  <li key={i} className="flex items-start text-sm">
                    <span className="text-red-400 mr-2 mt-0.5">⚠</span>
                    <span className="text-gray-700 dark:text-gray-300">{flag}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {/* Justifikasi Skor */}
          <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              <span className="font-semibold">Justifikasi Skor ({skor}/100):</span> {result.justifikasi_skor || '-'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};


// ============ MAIN COMPONENT ============
const FinSightContent = () => {
  const [file, setFile] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [currentModule, setCurrentModule] = useState(0);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  // Load history on mount
  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await fetch(getFullApiUrl('FINSIGHT_HISTORY') + '?limit=10', {
        headers: addAuthHeader({})
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success) setHistory(data.analyses || []);
      }
    } catch (err) {
      console.error('Error loading FinSight history:', err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const loadAnalysisDetail = async (id) => {
    try {
      const response = await fetch(getFullApiUrl('FINSIGHT_HISTORY') + `/${id}`, {
        headers: addAuthHeader({})
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.analysis) {
          setAnalysisResult(data.analysis);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }
    } catch (err) {
      console.error('Error loading analysis detail:', err);
    }
  };

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files?.[0]) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.name.toLowerCase().endsWith('.pdf')) {
        setFile(droppedFile);
      }
    }
  }, []);

  const handleFileSelect = (e) => {
    if (e.target.files?.[0]) setFile(e.target.files[0]);
  };

  const handleAnalyze = async () => {
    if (!file) return;
    setIsAnalyzing(true);
    setCurrentModule(1);
    setAnalysisResult(null);

    // Simulate progress (since backend runs synchronously)
    const progressInterval = setInterval(() => {
      setCurrentModule(prev => {
        if (prev >= 7) { clearInterval(progressInterval); return 7; }
        return prev + 1;
      });
    }, 8000); // ~8 seconds per module estimate

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (companyName.trim()) {
        // Append company_name as query param
      }

      const url = getFullApiUrl('FINSIGHT_ANALYZE') + (companyName.trim() ? `?company_name=${encodeURIComponent(companyName.trim())}` : '');
      const response = await fetch(url, {
        method: 'POST',
        headers: addAuthHeader({}),
        body: formData,
      });

      clearInterval(progressInterval);

      const data = await response.json();
      if (response.ok && data.success) {
        setCurrentModule(7);
        // Format result to match detail structure
        setAnalysisResult({
          id: data.analysis_id,
          company_name: data.company_name,
          status: data.status,
          results: data.results,
          created_at: new Date().toISOString(),
        });
        loadHistory(); // Refresh history
      } else {
        alert(data.detail || data.message || 'Analisis gagal');
      }
    } catch (err) {
      clearInterval(progressInterval);
      console.error('Analysis error:', err);
      alert('Terjadi kesalahan saat analisis');
    } finally {
      setIsAnalyzing(false);
      setCurrentModule(0);
    }
  };

  const handleReset = () => {
    setFile(null);
    setCompanyName('');
    setAnalysisResult(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('id-ID', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  // Extract module 7 data for display
  const finalAssessment = analysisResult?.results?.module_7?.final_assessment || {};
  const weightedScore = finalAssessment.weighted_score || {};
  const totalScore = weightedScore.total_score || 0;
  const riskBadge = getRiskBadge(finalAssessment.klasifikasi_risiko);
  const recStyle = getRecommendationStyle(finalAssessment.rekomendasi);

  return (
    <main className="p-6">
      <div className="space-y-6 pb-20">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center transition-colors">
                <i className="fa-solid fa-magnifying-glass-chart mr-3 text-indigo-500"></i>
                FinSight
              </h1>
              <p className="text-gray-600 dark:text-gray-300 mt-1 transition-colors">
                Credit Risk Analysis — Analisis risiko kredit dengan 7 modul AI
              </p>
            </div>
            {analysisResult && (
              <button onClick={handleReset} className="px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors text-sm">
                <i className="fa-solid fa-plus mr-2"></i>Analisis Baru
              </button>
            )}
          </div>
        </div>

        {/* ====== UPLOAD SECTION ====== */}
        {!analysisResult && !isAnalyzing && (
          <>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
              <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4 flex items-center transition-colors">
                <i className="fa-solid fa-cloud-arrow-up mr-2 text-indigo-500"></i>
                Upload Laporan Keuangan
              </h2>
              
              {/* Company Name Input */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nama Perusahaan (Opsional)</label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Akan diekstrak otomatis jika dikosongkan"
                  className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                />
              </div>

              {/* Upload Zone */}
              <div
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-xl p-8 text-center transition-all cursor-pointer ${
                  dragActive 
                    ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20' 
                    : file 
                      ? 'border-green-400 bg-green-50 dark:bg-green-900/20' 
                      : 'border-gray-300 dark:border-gray-600 hover:border-indigo-400 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
                onClick={() => !file && document.getElementById('finsight-file-input').click()}
              >
                <input
                  id="finsight-file-input"
                  type="file"
                  accept=".pdf"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                {file ? (
                  <div className="space-y-2">
                    <i className="fa-solid fa-file-pdf text-4xl text-green-500"></i>
                    <p className="text-sm font-medium text-gray-800 dark:text-white">{file.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                    <button
                      onClick={(e) => { e.stopPropagation(); setFile(null); }}
                      className="text-xs text-red-500 hover:text-red-700 underline"
                    >
                      Hapus file
                    </button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <i className="fa-solid fa-cloud-arrow-up text-4xl text-gray-400 dark:text-gray-500"></i>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Drag & drop file PDF atau <span className="text-indigo-600 dark:text-indigo-400 font-medium">klik untuk memilih</span>
                    </p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">Format: PDF (Neraca, Laba Rugi, Laporan Audit)</p>
                  </div>
                )}
              </div>

              {/* Analyze Button */}
              {file && (
                <button
                  onClick={handleAnalyze}
                  className="mt-4 w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold rounded-lg hover:from-indigo-700 hover:to-purple-700 transition-all flex items-center justify-center shadow-lg"
                >
                  <i className="fa-solid fa-wand-magic-sparkles mr-2"></i>
                  Mulai Analisis FinSight
                </button>
              )}
            </div>

            {/* How It Works */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
              <h2 className="text-base font-semibold text-gray-800 dark:text-white mb-4 flex items-center transition-colors">
                <i className="fa-solid fa-circle-info mr-2 text-indigo-500"></i>
                7 Modul Analisis AI
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {MODULE_LABELS.map((m) => (
                  <div key={m.num} className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg transition-colors">
                    <div className={`w-8 h-8 rounded-full bg-${m.color}-100 dark:bg-${m.color}-900/30 flex items-center justify-center flex-shrink-0`}>
                      <i className={`fa-solid ${m.icon} text-${m.color}-500 text-xs`}></i>
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-gray-800 dark:text-white">Modul {m.num}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{m.name}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* ====== PROCESSING STATE ====== */}
        {isAnalyzing && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-8 transition-colors">
            <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-6 text-center transition-colors">
              <i className="fa-solid fa-brain text-indigo-500 mr-2 animate-pulse"></i>
              AI sedang menganalisis...
            </h2>
            <div className="max-w-md mx-auto space-y-3">
              {MODULE_LABELS.map((m) => {
                const isDone = currentModule > m.num;
                const isActive = currentModule === m.num;
                const isPending = currentModule < m.num;
                return (
                  <div key={m.num} className={`flex items-center space-x-3 p-3 rounded-lg transition-all ${
                    isActive ? 'bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-700' :
                    isDone ? 'bg-green-50 dark:bg-green-900/20' : 'bg-gray-50 dark:bg-gray-700/30'
                  }`}>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${
                      isDone ? 'bg-green-500' : isActive ? 'bg-indigo-500' : 'bg-gray-300 dark:bg-gray-600'
                    }`}>
                      {isDone ? (
                        <i className="fa-solid fa-check text-white text-xs"></i>
                      ) : isActive ? (
                        <i className="fa-solid fa-spinner fa-spin text-white text-xs"></i>
                      ) : (
                        <span className="text-white text-xs font-bold">{m.num}</span>
                      )}
                    </div>
                    <span className={`text-sm ${
                      isDone ? 'text-green-700 dark:text-green-300 font-medium' :
                      isActive ? 'text-indigo-700 dark:text-indigo-300 font-semibold' :
                      'text-gray-400 dark:text-gray-500'
                    }`}>
                      {isActive ? PROGRESS_MESSAGES[m.num] : m.name}
                    </span>
                  </div>
                );
              })}
            </div>
            <p className="text-center text-xs text-gray-400 dark:text-gray-500 mt-6">
              Proses ini membutuhkan waktu ±1-2 menit
            </p>
          </div>
        )}

        {/* ====== RESULTS REPORT ====== */}
        {analysisResult && analysisResult.results && (
          <div className="space-y-6">
            {/* Report Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-lg shadow-lg p-6 text-white">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div>
                  <h2 className="text-xl font-bold">{analysisResult.company_name || 'FinSight Report'}</h2>
                  <p className="text-indigo-200 text-sm mt-1">
                    <i className="fa-solid fa-calendar mr-1"></i>
                    {formatDate(analysisResult.created_at)}
                  </p>
                </div>
                <span className={`px-4 py-2 rounded-full text-sm font-bold border ${riskBadge.bg} ${riskBadge.text} ${riskBadge.border}`}>
                  {riskBadge.label}
                </span>
              </div>
            </div>

            {/* Score Gauge + Recommendation */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Credit Score */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 flex flex-col items-center transition-colors">
                <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Weighted Credit Score</h3>
                <CreditScoreGauge score={totalScore} />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">Skor tertimbang dari 6 modul analisis</p>
              </div>

              {/* Recommendation */}
              <div className={`rounded-lg shadow-sm p-6 border-2 ${recStyle.bg} ${recStyle.border} transition-colors`}>
                <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase mb-3">Rekomendasi</h3>
                <div className="flex items-center space-x-3 mb-3">
                  <i className={`fa-solid ${recStyle.icon} text-3xl ${recStyle.iconColor}`}></i>
                  <span className="text-xl font-bold text-gray-800 dark:text-white">{finalAssessment.rekomendasi || 'N/A'}</span>
                </div>
                {finalAssessment.syarat_conditional && finalAssessment.syarat_conditional.length > 0 && finalAssessment.syarat_conditional[0] !== '...' && (
                  <div className="mt-3">
                    <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase mb-1">Syarat:</p>
                    <ul className="space-y-1">
                      {finalAssessment.syarat_conditional.map((s, i) => (
                        <li key={i} className="flex items-start text-sm text-gray-700 dark:text-gray-300">
                          <span className="text-amber-500 mr-2">•</span>{s}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>

            {/* Executive Summary */}
            {finalAssessment.ringkasan_eksekutif && (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
                <h3 className="text-md font-semibold text-gray-800 dark:text-white mb-3 flex items-center transition-colors">
                  <i className="fa-solid fa-file-lines mr-2 text-indigo-500"></i>
                  Ringkasan Eksekutif
                </h3>
                <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">{finalAssessment.ringkasan_eksekutif}</p>
              </div>
            )}

            {/* Module Scorecard */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden transition-colors">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-md font-semibold text-gray-800 dark:text-white flex items-center transition-colors">
                  <i className="fa-solid fa-table mr-2 text-indigo-500"></i>
                  Module Scorecard
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-700/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Modul</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Skor</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Bobot</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">Kontribusi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {[
                      { key: 'module_1_liquidity', label: 'Likuiditas & Arus Kas' },
                      { key: 'module_2_debt_service', label: 'Kemampuan Bayar Cicilan' },
                      { key: 'module_3_profitability', label: 'Profitabilitas' },
                      { key: 'module_4_capital_structure', label: 'Struktur Modal' },
                      { key: 'module_5_receivables', label: 'Kualitas Piutang' },
                      { key: 'module_6_qualitative', label: 'Faktor Kualitatif' },
                    ].map((mod) => {
                      const ws = weightedScore[mod.key] || {};
                      return (
                        <tr key={mod.key} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                          <td className="px-4 py-3 text-sm text-gray-800 dark:text-white">{mod.label}</td>
                          <td className="px-4 py-3 text-center text-sm font-bold" style={{ color: getScoreColor(ws.skor || 0) }}>{ws.skor || 0}</td>
                          <td className="px-4 py-3 text-center text-sm text-gray-500 dark:text-gray-400">{((ws.bobot || 0) * 100).toFixed(0)}%</td>
                          <td className="px-4 py-3 text-center text-sm font-medium text-gray-700 dark:text-gray-300">{typeof ws.kontribusi === 'number' ? ws.kontribusi.toFixed(1) : ws.kontribusi || 0}</td>
                        </tr>
                      );
                    })}
                    <tr className="bg-indigo-50 dark:bg-indigo-900/20 font-bold">
                      <td className="px-4 py-3 text-sm text-indigo-700 dark:text-indigo-300">TOTAL</td>
                      <td className="px-4 py-3 text-center text-sm text-indigo-700 dark:text-indigo-300" colSpan={2}></td>
                      <td className="px-4 py-3 text-center text-lg text-indigo-700 dark:text-indigo-300">{typeof totalScore === 'number' ? totalScore.toFixed(1) : totalScore}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Strengths & Risks */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Strengths */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
                <h3 className="text-md font-semibold text-gray-800 dark:text-white mb-3 flex items-center transition-colors">
                  <i className="fa-solid fa-shield-check text-green-500 mr-2"></i>
                  Kekuatan Utama
                </h3>
                <ul className="space-y-2">
                  {(finalAssessment.kekuatan_utama || []).filter(k => k !== '...').map((item, i) => (
                    <li key={i} className="flex items-start text-sm">
                      <span className="text-green-500 mr-2 mt-0.5 font-bold">{i + 1}.</span>
                      <span className="text-gray-700 dark:text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Risks */}
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 transition-colors">
                <h3 className="text-md font-semibold text-gray-800 dark:text-white mb-3 flex items-center transition-colors">
                  <i className="fa-solid fa-triangle-exclamation text-red-500 mr-2"></i>
                  Risiko Utama
                </h3>
                <ul className="space-y-2">
                  {(finalAssessment.risiko_utama || []).filter(r => r !== '...').map((item, i) => (
                    <li key={i} className="flex items-start text-sm">
                      <span className="text-red-500 mr-2 mt-0.5 font-bold">{i + 1}.</span>
                      <span className="text-gray-700 dark:text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Module Detail Accordion */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden transition-colors">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-md font-semibold text-gray-800 dark:text-white flex items-center transition-colors">
                  <i className="fa-solid fa-layer-group mr-2 text-indigo-500"></i>
                  Detail Analisis per Modul
                </h3>
              </div>
              <div className="p-4 space-y-2">
                {MODULE_LABELS.slice(0, 6).map((label) => (
                  <ModuleAccordion
                    key={label.num}
                    moduleNum={label.num}
                    result={analysisResult.results?.[`module_${label.num}`]}
                    label={label}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ====== HISTORY SECTION ====== */}
        {!isAnalyzing && !analysisResult && history.length > 0 && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden transition-colors">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-800 dark:text-white flex items-center transition-colors">
                <i className="fa-solid fa-clock-rotate-left mr-2 text-indigo-500"></i>
                Riwayat Analisis
              </h2>
            </div>
            <div className="p-6">
              {loadingHistory ? (
                <div className="text-center py-8">
                  <i className="fa-solid fa-spinner fa-spin text-2xl text-gray-400"></i>
                </div>
              ) : (
                <div className="space-y-3">
                  {history.map((item) => {
                    const badge = getRiskBadge(item.klasifikasi_risiko);
                    return (
                      <div
                        key={item.id}
                        className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-indigo-300 dark:hover:border-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors cursor-pointer"
                        onClick={() => loadAnalysisDetail(item.id)}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-3 mb-1">
                            <h3 className="font-medium text-gray-800 dark:text-white truncate">{item.company_name}</h3>
                            {item.klasifikasi_risiko && (
                              <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${badge.bg} ${badge.text}`}>
                                {badge.label}
                              </span>
                            )}
                            {item.status === 'failed' && (
                              <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-red-100 text-red-700">FAILED</span>
                            )}
                          </div>
                          <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                            <span><i className="fa-solid fa-calendar mr-1"></i>{formatDate(item.created_at)}</span>
                            {item.total_score !== null && item.total_score !== undefined && (
                              <span><i className="fa-solid fa-star mr-1"></i>Score: {typeof item.total_score === 'number' ? item.total_score.toFixed(1) : item.total_score}</span>
                            )}
                            {item.rekomendasi && (
                              <span><i className="fa-solid fa-gavel mr-1"></i>{item.rekomendasi}</span>
                            )}
                          </div>
                        </div>
                        <button className="ml-4 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm rounded-lg transition-colors flex items-center">
                          <i className="fa-solid fa-eye mr-2"></i>Lihat
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </main>
  );
};

export default FinSightContent;
